import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './useAuth';
import { FullPageSpinner } from '../components/Spinner';

/**
 * Gate for everything behind the sign-in screen.
 *
 * `adminOnly` is the default: this is a back-office app. While an admin is
 * impersonating a student, their role *is* `student`, so those routes
 * correctly become unavailable and the UI sends them to the impersonation
 * workspace instead — the same rule the API enforces, surfaced in the
 * navigation rather than as a wall of 403s.
 */
export function ProtectedRoute({
  children,
  adminOnly = true,
}: {
  children: ReactNode;
  adminOnly?: boolean;
}) {
  const { user, ready, isImpersonating } = useAuth();
  const location = useLocation();

  if (!ready) return <FullPageSpinner label="Restoring your session…" />;

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to={isImpersonating ? '/acting-as' : '/login'} replace />;
  }

  return <>{children}</>;
}
